import store from "@/store";
import axios from "axios";
import Config from "../config/index";
import { appendObjectAsQueryParam } from "./utils/index";

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */
// axios.defaults.baseURL = process.env.API_URL;
// axios.defaults.timeout = 300000;
// timeout is 5 mins. Some developers suffer from slow upload speed.

/**
 * Add a request interceptor authentication purposes.
 * Each request must set an Authentication header containing the JWT token.
 * The JWT token is given by the server on user login and set in localStorage.
 */
axios.interceptors.request.use(
  config => {
    if (store.state.token) {
      config.headers["Authorization"] = "Bearer " + store.state.token;
    }

    // config.headers["X-Requested-With"] = "XMLHttpRequest";
    // config.headers["Expires"] = "-1";
    // config.headers["Cache-Control"] =
    //   "no-cache,no-store,must-revalidate,max-age=-1,private";
    return config;
  },
  error => {
    console.error("Request failed.");
    return Promise.reject(error);
  }
);

/**
 * Add a response interceptor.
 */
axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response) {
      // Logout our user, they are not authorized.
      if (error.response.status === 401) {
        store.dispatch("logoutUser", true);
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the
      // browser and an instance of http.ClientRequest in node.js.
      console.error(error.request);
    } else {
      // Something happened in setting up the request
      // that triggered an Error.
      console.error("Error", error.message);
    }
    console.error(error);
    return Promise.reject(error);
  }
);

export default {
  /**
   * loginUser
   * Login our user, receive a JWT token and set it.
   *
   * https://jwt.io/introduction/
   * In authentication, when the user successfully logs in using their
   * credentials, a JSON Web Token will be returned and must be saved
   * locally (typically in local storage, but cookies can be also used),
   * instead of the traditional approach of creating a session in the
   * server and returning a cookie.
   *
   * @param {Object} credentials
   * @return {Promise.<TResult>}
   */
  loginUser: credentials => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/login`, credentials)
      .then(response => response.data);
  },

  /**
   * loginUserAzc
   * Login our azerion connect user, receive a JWT token and set it.
   *
   * @param {Object} params
   * @return {Promise.<TResult>}
   */
  loginUserAzc: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/login-azc`, params)
      .then(response => response.data);
  },

  /**
   * loginGuest
   * Login our guest user, receive a JWT token and set it.
   *
   * @param {Object} params
   * @return {Promise.<TResult>}
   */
  loginGuest: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/guest/token`, params)
      .then(response => response.data);
  },

  /**
   * logoutAzc
   * Logout user from AZC
   *
   * @param {Object} params
   * @return {Promise.<TResult>}
   */
  logutAzc: params => {
    const qs = require("qs");
    const data = qs.stringify({
      client_id: Config.AZC_AUTH_CLIENT_ID,
      refresh_token: params.azc_refresh_token
    });
    return axios
      .post(
        `${Config.AZC_AUTH_URL}/realms/azerion/protocol/openid-connect/logout`,
        data,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${params.azc_access_token}`
          }
        }
      )
      .then(response => {
        console.log(response);
      });
  },

  /**
   * loginFacebook
   * Login our user using his/her facebook account.
   * Get token from our auth and continue as usual
   * @param {Promise} store
   * @param {Object} credentials
   * @return {Promise}
   */
  loginFacebook: credentials => {
    return axios
      .post(`${Config.PLAY_API_URL}/oauth/facebook/token`, credentials)
      .then(response => response.data);
  },

  /**
   * registerUser
   * Register a new user
   *
   * @param {Object} credentials
   * @return {Promise.<TResult>}
   */
  registerUser: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/register`, params)
      .then(response => response.data);
  },

  /**
   * forgetPassword
   * Initiate a password reset process.
   * @param {Object} params
   */
  forgetPassword: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/forgot-password/request`, params)
      .then(response => response.data);
  },

  /**
   * resetPassword
   * Submits the new assigned password
   * @param {Object} params
   */
  resetPassword: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/forgot-password/reset`, params)
      .then(response => response.data);
  },

  /**
   * fetchGameConfig
   * Login our user, receive a JWT token and set it.
   * @param {String} game
   * @return {Promise.<TResult>}
   */
  fetchGameConfig: (game, domain) => {
    return axios
      .get(
        `${Config.CONFIG_API_URL}/${game}?domain=${
          domain ? domain : "gamedistribution.com"
        }`,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      )
      .then(response => response.data);
  },

  /**
   * fetchUserFriends
   * Fetch friends of our user
   * @param {Object} params
   * @return {Promise.<TResult>}
   */
  fetchFriends: gameId => {
    return axios
      .get(`${Config.PLAY_API_URL}/friend/${gameId}/list`)
      .then(response => response.data);
  },

  /**
   * fetchPlayingUsers
   * Fetches the list of the users who play the game.
   * @param {string} gameId
   * @param {Object} queryParams
   * @return {Promise.<TResult>}
   */
  fetchPlayingUsers: (gameId, queryParams) => {
    const url = appendObjectAsQueryParam(
      `${Config.PLAY_API_URL}/friend/${gameId}/user/list`,
      queryParams
    );
    return axios.get(url).then(response => response.data);
  },

  /**
   * fetchFriendRequests
   * Fetches the list of the users who invited you.
   * @param {Object} params
   * @return {Promise.<TResult>}
   */
  fetchFriendRequests: gameId => {
    return axios
      .get(`${Config.PLAY_API_URL}/friend/${gameId}/pending/reply/list`)
      .then(response => response.data);
  },

  /**
   * inviteUser
   * Invite a user to be friends in the game.
   * @param {Number} gameId
   * @param {Number} userId
   */
  inviteUser: (gameId, userId) => {
    return axios
      .post(`${Config.PLAY_API_URL}/friend/${gameId}/${userId}/request`)
      .then(response => response.data);
  },

  /**
   * inviteUserViaEmail
   * Invite a user to play the game.
   * @param {Object} params
   */
  inviteUserViaEmail: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/invite/email`, params)
      .then(response => response.data);
  },

  /**
   * acceptFriendRequest
   * Accepts the friend request coming from a user.
   * @param {Number} gameId
   * @param {Number} friendId
   * @param {Object} store
   */
  acceptFriendRequest: (gameId, friendId) => {
    return axios
      .post(`${Config.PLAY_API_URL}/friend/${gameId}/${friendId}/accept`)
      .then(response => response.data);
  },

  /**
   * declineFriendRequest
   * Declines the friend request coming from a user.
   * @param {Number} gameId
   * @param {Number} friendId
   */
  declineFriendRequest: (gameId, friendId) => {
    return axios
      .post(`${Config.PLAY_API_URL}/friend/${gameId}/${friendId}/decline`)
      .then(response => response.data);
  },

  /**
   * deleteFriend
   * Deletes a friend from friend list
   * @param {Number} gameId
   * @param {Number} friendId
   */
  deleteFriend: (gameId, friendId) => {
    return axios
      .post(`${Config.PLAY_API_URL}/friend/${gameId}/${friendId}/delete`)
      .then(response => response.data);
  },

  /**
   * fetchUserInfo
   * Fetch friends of our user
   * @return {Promise.<TResult>}
   */
  fetchUserInfo: () => {
    return axios
      .get(`${Config.PLAY_API_URL}/user/info`)
      .then(response => response.data);
  },

  /**
   * fetchPaymentInfo
   * Fetch payment info of our user to check if it is first time buyer
   * @param {Object} options
   * @return {Promise.<TResult>}
   */
  fetchPaymentInfo: options => {
    return axios
      .post(`${Config.PAYMENT_API_URL}/user/payment-info`, {
        userid: options.userid,
        platform: options.platform,
        game: options.game
      })
      .then(response => response.data);
  },

  /**
   * updateUserSettings
   * update user related settings
   * @return {Promise.<TResult>}
   */
  updateUserSettings: settings => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/me/update`, settings)
      .then(response => response.data);
  },

  /**
   * fetchUserDownloadableDataLink
   * Fetch all user data of the user
   * @return {Promise.<TResult>}
   */
  fetchUserDownloadableDataLink: () => {
    return axios
      .get(`${Config.PLAY_API_URL}/user/me/download/link`)
      .then(response => response.data);
  },

  /**
   * deleteUserAccount
   * Attempt to delete all user related data
   * @return {Promise.<TResult>}
   */
  deleteUserAccount: () => {
    return axios
      .delete(`${Config.PLAY_API_URL}/user/me/delete`)
      .then(response => response.data);
  },

  /**
   * fetchTranslation
   * Fetch a translations included json file
   * @param {Number} lang
   * @return {Promise.<TResult>}
   */
  fetchTranslation: lang => {
    return axios
      .get(`${Config.ASSET_URL}/translations/${lang}.json`)
      .then(response => response.data);
  },
  /**
   * updateEmail/Request
   * send code to change e-mail address
   * @param {Object} params
   */
  updateEmailRequest: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/me/update-email/request`, params)
      .then(response => response.data);
  },

  /**
   * updateEmail
   * Email verification will be provided
   * @param {Object} params
   */
  updateEmail: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/me/update-email`, params)
      .then(response => response.data);
  },

  /**
   * sessionateGameUser
   * Fetches hashvalues for game user session
   * We use hashed value to authenticate on the game side
   * @param {Number} lang
   * @return {Promise.<TResult>}
   */
  sessionateGameUser: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/sessionate`, params)
      .then(response => response.data);
  },

  /**
   * trackPlayingUser
   * Tracks playing user using auth api for friends system
   * @return {Promise.<TResult>}
   */
  trackPlayingUser: () => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/play`, {
        gameId: store.getters.getGame.plingaGameId
      })
      .then(response => response.data);
  },

  /**
   * sendZendeskTicket
   * Sends a ticket to zendesk
   * @param {*} data
   * @return {Promise.<TResult>}
   */
  sendZendeskTicket: data => {
    return axios
      .post(`${Config.PLAY_API_URL}/user/support`, data)
      .then(response => response.data);
  },
  /**
   * searchPlayingUser
   * users playing the game
   * @param {Number} gameId
   * @param {Object} queryParams
   *  * @return {Promise.<TResult>}
   */
  searchPlayingUser: (gameId, queryParams) => {
    const url = appendObjectAsQueryParam(
      `${Config.PLAY_API_URL}/friend/${gameId}/user/search`,
      queryParams
    );
    return axios.get(url).then(response => response.data);
  },
  /**
   * See Notification
   * @param {Object} params
   */
  seeNotification: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/game/notification/seen/all`, {
        notificationIdList: params.notificationIdList,
        gameName: params.gameName
      })
      .then(response => response.data);
  },
  /**
   * Fetch notifications
   * @param {Object} params
   * @return {Promise.<TResult>}
   */
  fetchNotifications: params => {
    return axios
      .get(
        `${Config.PLAY_API_URL}/game/notification/${params.gameName}?size=6&page=${params.page}&locale=${params.locale}`
      )
      .then(response => response.data);
  },
  /**
   * Delete Notification request
   * @param {object} params
   * @return {Promise.<TResult>}
   */
  deleteNotifications: params => {
    console.log("paramsApi", params);
    return axios
      .post(`${Config.PLAY_API_URL}/game/notification/remove/all`, {
        notificationIdList: params.notificationIdList,
        gameName: params.gameName
      })
      .then(response => response.data);
  },
  /**
   * Get Game Notification Total Unseen
   * @param {Object} params
   * @return {Promise.<TResult>}
   */
  gameNotificationTotalUnseen: params => {
    return axios
      .get(
        `${Config.PLAY_API_URL}/game/notification/${params.gameName}/total-unseen`
      )
      .then(response => response.data);
  },

  /**
   * Fetch campaigns
   * Fetch revive campaigns through the API
   * as Adblockers block the request on browsers
   * @param {Object} params
   */
  fetchCampaigns: params => {
    return axios
      .post(`${Config.PLAY_API_URL}/game/campaign/check`, params)
      .then(response => response.data);
  }
};
